import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDateFromISO } from '@tenant/utility/helper/DateHelper';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  inputFormatDateTime,
} from '@tenant/utility/constants/default';
import useGetRepositoryVersions from 'redux/slices/dockerImage/hooks/useGetRepositoryVersions';
import { PAGE_SIZE } from 'constants/Common';
import RefreshIcon from '@mui/icons-material/Refresh';
import PropTypes from 'prop-types';

const DockerImageVersion = ({ repository }) => {
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX);
  const [totalCount, setTotalCount] = useState(0);

  const { data, isLoading, isRefetching, refetch } = useGetRepositoryVersions({
    repository,
    pageIndex: pageIndex,
    pageSize: DEFAULT_PAGE_SIZE,
    searchTerm: '',
  });

  useEffect(() => {
    if (typeof data?.data?.TotalCount === 'number') {
      setTotalCount(data?.data?.TotalCount ?? 0);
    }
  }, [data?.data?.TotalCount]);

  const columns = [
    {
      field: 'ImageFullName',
      headerName: 'Image Name',
      width: 450,
      sortable: true,
      flex: 1,
    },
    {
      field: 'ImageId',
      headerName: 'Image ID',
      width: 450,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography
            variant='body2'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'Tag',
      headerName: 'Tag',
      width: 75,
      sortable: true,
    },
    {
      field: 'CreatedAt',
      headerName: 'Created On',
      width: 150,
      sortable: false,
      renderCell: (params) =>
        params.value
          ? formatDateFromISO(params.value, inputFormatDateTime)
          : null,
    },
  ];

  const onPageChange = (page) => {
    setPageIndex(page);
  };

  return (
    <>
      <Box sx={{ height: 'auto' }}>
        <DataGrid
          hideFooter
          autoHeight
          disableColumnSelector
          disableColumnMenu
          rows={data?.data?.Items ?? []}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={isLoading || isRefetching}
        />
      </Box>

      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        mt={2}
        className='footerContainer'
      >
        <Pagination
          count={Math.ceil(totalCount / PAGE_SIZE) || 1}
          page={pageIndex}
          onChange={(e, v) => onPageChange(v)}
          showFirstButton
          showLastButton
        />
        <Typography>Total: {totalCount || 0}</Typography>
        <Tooltip title='Refresh'>
          <IconButton
            disabled={isRefetching}
            size='small'
            sx={{ ml: 2 }}
            onClick={refetch}
          >
            <RefreshIcon fontSize='inherit' />
          </IconButton>
        </Tooltip>
      </Box>
    </>
  );
};

DockerImageVersion.propTypes = {
  repository: PropTypes.string,
};

export default React.memo(DockerImageVersion);
